import Phaser from 'phaser'
import { hasMsg, type JKGameMsgKey, t, weaponName } from '../../i18n'
import { apiAccountWallet, apiEconomyConfig, apiUnlockChar, type GachaItem, getHandle, RARITY_COLOR, type Rarity, rarityLabel } from '../../services/JKGame_economy'
import { getActiveChar, setActiveChar } from '../../services/JKGame_loadout'
import { getSouls, isUnlocked, soulCost, syncFromBackend, unlockChar } from '../../services/JKGame_souls'
import { PACK_ROSTER } from '../../sim/JKGame_pack'
import { audio } from '../JKGame_audio'
import { getEquippedChar, setEquippedChar } from '../JKGame_cosmetics'
import { COLORS } from '../JKGame_ui'
import { mountCollectionOverlay, type CollectionOverlayHandle, type CollectionOwnedRow } from '../../ui/JKGame_collectionOverlay'

// Localized character display name (char.<id>.name), falling back to the raw id so a missing
// translation never blanks the row (UX: never a silent/raw key).
function charName(id: string): string {
  const k = `char.${id}.name`
  return hasMsg(k) ? t(k as JKGameMsgKey) : id
}

const RARITY_ORDER: Record<Rarity, number> = { SSR: 0, SR: 1, R: 2 }

// Read-only collection: the cosmetics the player owns (from the persistent account wallet),
// matched against the gacha POOL for name/rarity. Makes a gacha pull MEAN something — you can
// see what you've collected. Cosmetic-only (no stats) → PVP-fair. Equipping is a later slice.
// Degrades to a localized notice when the account/economy endpoints are unreachable (offline).
export class JKGame_CollectionScene extends Phaser.Scene {
  private alive = true // false after SHUTDOWN; guards the async fetch callback
  private overlay?: CollectionOverlayHandle
  private pool: GachaItem[] = [] // cached so an equip tap re-renders without re-fetching
  private owned: Record<string, number> = {}
  private backend = false // true once the authoritative wallet loaded → souls/unlock go through the server
  private readonly keyEsc = (): void => {
    this.scene.start('Menu')
  }

  constructor() {
    super('Collection')
  }

  create(): void {
    this.alive = true
    this.overlay?.destroy()
    this.overlay = undefined
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false
      this.overlay?.destroy()
      this.overlay = undefined
      this.input.keyboard?.off('keydown-ESC', this.keyEsc)
    })
    this.cameras.main.setBackgroundColor(COLORS.bg)
    this.cameras.main.fadeIn(240, 0, 0, 0)

    this.overlay = mountCollectionOverlay({
      title: t('collection.title'),
      rosterTitle: t('collection.activeTitle'),
      tapEquip: t('collection.tapEquip'),
      tapEquipShort: t('collection.tapEquipShort'),
      loadingText: t('menu.loading'),
      backLabel: t('menu.back'),
      onBack: () => this.scene.start('Menu'),
      onRosterTap: (id) => this.onRosterTap(id),
      onOwnedTap: (id) => this.onOwnedTap(id),
    })
    this.renderRoster()
    this.overlay.setOwned(t('menu.loading'), [])
    this.input.keyboard?.off('keydown-ESC', this.keyEsc).on('keydown-ESC', this.keyEsc) // desktop back

    void this.init()
  }

  private async init(): Promise<void> {
    try {
      const [cfg, wallet] = await Promise.all([apiEconomyConfig(), apiAccountWallet(getHandle())])
      if (!this.alive) return // navigated away mid-request
      this.pool = cfg.gacha.pool
      this.owned = wallet.owned
      // Authoritative wallet reached → mirror its souls + unlocked into localStorage so the roster
      // selector (and deploy gating) reflect server truth, then switch the roster to backend unlocks.
      this.backend = true
      syncFromBackend(wallet.souls, wallet.unlocked ?? [])
      this.renderRoster()
      this.render()
    } catch {
      if (!this.alive) return
      this.overlay?.setOwned(t('collection.offline'), []) // account/economy unreachable — never a silent blank
    }
  }

  private render(): void {
    const byId = new Map(this.pool.map((it) => [it.id, it]))
    // Owned pool entries with a positive count, rarest first then by id (deterministic order).
    const rows = Object.entries(this.owned)
      .filter(([id, n]) => n > 0 && byId.has(id))
      .map(([id, n]) => ({ item: byId.get(id) as GachaItem, count: n }))
      .sort((a, b) => RARITY_ORDER[a.item.rarity] - RARITY_ORDER[b.item.rarity] || a.item.id.localeCompare(b.item.id))

    if (rows.length === 0) {
      this.overlay?.setOwned(t('collection.empty'), []) // owns nothing yet → nudge toward gacha
      return
    }
    const equipped = getEquippedChar()
    const ownedRows: CollectionOwnedRow[] = rows.map((r) => {
      const color = RARITY_COLOR[r.item.rarity]
      const isChar = r.item.kind === 'char'
      const isEquipped = isChar && r.item.id === equipped
      return {
        id: r.item.id,
        rarity: rarityLabel(r.item.rarity),
        name: r.item.name,
        count: r.count,
        color,
        isChar,
        equipped: isEquipped,
        rightLabel: isEquipped ? `● ${t('collection.equipped')}` : `×${r.count}`,
      }
    })
    this.overlay?.setOwned(t('collection.tapEquip'), ownedRows)
  }

  // 出戰角色 selector. Renders from the local roster pack (no backend needed), so a player can
  // pick a character and immediately feel the difference in the next match (start weapon + passive).
  // Characters unlock with souls (跨局永久幣, earned at settlement); free ones are always available.
  // Tapping an unlocked card deploys it; tapping a souls-locked card spends souls to unlock it (then
  // auto-deploys). Failed taps show a localized reason in rosterNote (never a silent failure).
  private renderRoster(): void {
    const active = getActiveChar()
    const rows = PACK_ROSTER.map((c) => {
      const unlocked = isUnlocked(c.id)
      const isActive = unlocked && c.id === active
      const cost = soulCost(c.unlock) // number for souls-path chars, else null (e.g. gacha)
      const passiveK = `passive.${c.passive}`
      const passiveLabel = hasMsg(passiveK) ? t(passiveK as JKGameMsgKey) : c.passive
      let state: string
      if (isActive) state = `● ${t('collection.deploying')}`
      else if (unlocked) state = '✓'
      else if (cost !== null) state = t('collection.unlockCost', { n: cost })
      else state = t('collection.gachaOnly')
      return {
        id: c.id,
        name: charName(c.id),
        detail: `${weaponName(c.startWeapon.name)}　·　${passiveLabel}`,
        state,
        unlocked,
        active: isActive,
        affordable: cost !== null && getSouls() >= cost,
      }
    })
    this.overlay?.setRoster(t('collection.souls', { n: getSouls() }), rows)
  }

  private onRosterTap(charId: string): void {
    const c = PACK_ROSTER.find((r) => r.id === charId)
    if (!c) return
    const unlocked = isUnlocked(c.id)
    const isActive = unlocked && c.id === getActiveChar()
    if (isActive) return
    if (unlocked) {
      audio.play('uiTap')
      setActiveChar(c.id)
      this.overlay?.setNote('')
      this.renderRoster()
      return
    }
    const cost = soulCost(c.unlock)
    if (cost !== null) {
      if (this.backend) { void this.unlockViaBackend(c.id); return }
      const res = unlockChar(c.id)
      if (res.ok) {
        audio.play('success')
        setActiveChar(c.id)
        this.overlay?.setNote('')
        this.renderRoster()
      } else {
        audio.play('error')
        this.overlay?.setNote(res.reason === 'cantAfford' ? t('collection.cantAfford') : t('collection.gachaOnlyNote'))
      }
      return
    }
    audio.play('error')
    this.overlay?.setNote(t('collection.gachaOnlyNote'))
  }

  private onOwnedTap(itemId: string): void {
    const byId = new Map(this.pool.map((it) => [it.id, it]))
    const item = byId.get(itemId)
    if (!item || item.kind !== 'char') return
    const isEquipped = item.id === getEquippedChar()
    audio.play('uiTap')
    setEquippedChar(isEquipped ? null : item.id)
    this.render()
  }

  // Backend-mode unlock: spend souls SERVER-side (authoritative — souls can't be forged), mirror the
  // returned wallet into localStorage so the roster + deploy gating reflect it, then auto-deploy.
  // A failure shows a localized reason (cantAfford / generic) — never a silent no-op.
  private async unlockViaBackend(charId: string): Promise<void> {
    try {
      const res = await apiUnlockChar(getHandle(), charId)
      if (!this.alive) return
      if (res.ok && res.wallet) {
        audio.play('success')
        syncFromBackend(res.wallet.souls, res.wallet.unlocked ?? [])
        setActiveChar(charId) // unlock → auto-deploy
        this.overlay?.setNote('')
        this.renderRoster()
      } else {
        audio.play('error')
        this.overlay?.setNote(res.reason === 'cantAfford' ? t('collection.cantAfford') : t('collection.gachaOnlyNote'))
      }
    } catch {
      if (!this.alive) return
      audio.play('error')
      this.overlay?.setNote(t('collection.offline')) // server unreachable — localized, not silent
    }
  }
}
